import {
    approvePaymentNoticeSummary,
    createOrUpdatePaymentNoticeSummary,
    fetchEstatePaymentNoticeSummary,
    fetchPaymentNoticeSummaries,
    setPaymentNoticeSummaryAsReadyForReview,
    unapprovePaymentNoticeSummary,
} from '@/api/purchasing.api';
import type { IPaymentNoticeSummary, PaymentNoticeSummary } from '@condo/domain';
import { useMutation, useQuery } from '@tanstack/vue-query';
import { computed, isRef } from 'vue';

export const useEstatePaymentNoticeSummary = estateId => {
    const query = useQuery(['payment-notice-summary', estateId], async (): Promise<PaymentNoticeSummary> => fetchEstatePaymentNoticeSummary(estateId), {
        initialData: null,
    });

    const { mutateAsync: createOrUpdateMutation } = useMutation(
        async (paymentNoticeSummary: Partial<IPaymentNoticeSummary>) => createOrUpdatePaymentNoticeSummary(estateId, paymentNoticeSummary),
        {
            onSuccess: () => {
                query.refetch();
            },
        },
    );

    const { mutateAsync: approveMutation } = useMutation(async () => approvePaymentNoticeSummary(query.data.value.paymentNoticeSummaryId), {
        onSuccess: () => {
            query.refetch();
        },
    });

    const { mutateAsync: unapproveMutation } = useMutation(async () => unapprovePaymentNoticeSummary(query.data.value.paymentNoticeSummaryId), {
        onSuccess: () => {
            query.refetch();
        },
    });

    const { mutateAsync: setAsReadyForReviewMutation } = useMutation(
        async () => setPaymentNoticeSummaryAsReadyForReview(query.data.value.paymentNoticeSummaryId),
        {
            onSuccess: () => {
                query.refetch();
            },
        },
    );

    return {
        ...query,
        createOrUpdate: createOrUpdateMutation,
        approve: approveMutation,
        unapprove: unapproveMutation,
        setAsReadyForReview: setAsReadyForReviewMutation,
    };
};

export const usePaymentNoticeSummaries = (queryOrRef: any) => {
    const query = computed(() => (isRef(queryOrRef) ? queryOrRef.value : queryOrRef));
    const queryEnabled = computed(() => !!query.value);

    return useQuery(
        ['payment-notice-summaries', query],
        async () => {
            return await fetchPaymentNoticeSummaries(query.value);
        },
        {
            enabled: queryEnabled,
            initialData: [],
        },
    );
};
